import React, { ReactNode, useEffect, useMemo } from "react";
import { ThemeProvider } from "styled-components";

import { FAVICON_CDN } from "@/config";
import IFrameService from "@/service/IFrame";
import InteractionService from "@/service/Interaction";
import { Interaction, InteractionResult } from "@/service/Model";
import ResizeService from "@/service/Resize";
import TrackingService from "@/service/Tracking";
import { GlobalStyles, themes } from "@/styles";

type AppProps = {
  themeKey: string;
  interaction: Interaction;
  interactionResults?: InteractionResult;
  onDestroy: () => void;
  children?: ReactNode;
};

export type ReactComponent<P = {}> =
  | React.ComponentClass<P>
  | React.FunctionComponent<P>;

const App = (Container: ReactComponent<any>) => {
  const AppComponent: React.FC<AppProps> = (
    props: AppProps
  ): React.ReactElement => {
    const { themeKey, interaction } = props;
    const { type, layout } = interaction;

    useEffect(() => {
      if (!interaction) {
        return;
      }
      // Set document title
      document.title = interaction.title;

      // Apply favicon
      const link: HTMLLinkElement =
        document.querySelector("link[rel*='icon']") ||
        document.createElement("link");
      link.type = "image/x-icon";
      link.rel = "shortcut icon";
      link.href = layout
        ? `${FAVICON_CDN}/${type}/${layout}/${themeKey}/favicon.ico`
        : `${FAVICON_CDN}/${type}/${themeKey}/favicon.ico`;
      document.head.appendChild(link);

      // Initialize services
      TrackingService.getInstance().interaction = interaction;
      IFrameService.initialized();
      IFrameService.getTealiumConfig();
      IFrameService.getCanonical();
      ResizeService.init((height: number) => IFrameService.resize(height), 200);
    }, [interaction]);

    const theme = useMemo(() => {
      const selectedTheme = themes[themeKey] || themes.default;
      if (!InteractionService.hasBackgroundImage(interaction)) {
        // Use dynamic height
        selectedTheme.globals.minInteractionHeight = "0";
      }
      return selectedTheme;
    }, [themeKey, interaction]);

    return (
      <ThemeProvider theme={theme}>
        <React.Fragment>
          <GlobalStyles fonts={themeKey} theme={theme} />
          <Container {...props} />
        </React.Fragment>
      </ThemeProvider>
    );
  };
  return AppComponent;
};

export default App;
